import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import { CACHE_QUERY_OPTIONS } from "../../../../../constants";
import {
    getClientTagByName,
    getAllRoomPreset,
    updateClientTag,
    getUnityPopups,
} from "../../../../../api/ClientsTagController";
import { getRooms } from "../../rooms/api/roomController";
import { getAchievements } from "../../../../../api/AchievementController";
import { uploadFile, getPresignedUrl } from "../../../../../api/FilesController";
import Typography from "../../../../../components/common/text/Typography";
import { setColor, setFont } from "../../../../../assets/styles/styles";
import Button from "../../../../../components/common/button/Button";
import Dropdown from "../../../../../components/common/inputDropdown/Dropdown";
import JsonViewerModal from "../../vanityLinks/components/JsonViewerModal";
import UploadFile from "../../../../../components/common/fileUploader/FileUploaderInput";
import ToggleSwitch from "../../../../../components/common/switchToggle/ToggleSwitch";
import Modal from "../../../components/Modal";
import Banner from "../../../../../components/layout/ui/Banner";
import LoadingIcon from "../../../../../components/common/loading/LoadingIcon";
import RoomSearchDropdown from "./RoomSearchDropdown";

const EditTag = ({ closeModal, data }) => {
    // TOKEN for API calls
    const token = useSelector((state) => state?.user?.token);

    const [activeTab, setActiveTab] = useState("CLIENT TAG");
    const tabs = ["CLIENT TAG", "POP UP SETTING"];

    // state for editing client tag
    const [isJsonEditorOpen, setIsJsonEditorOpen] = useState(false);
    const [jsonEditorValue, setJsonEditorValue] = useState("");
    const [buttonDisabled, setButtonDisabled] = useState(true);

    // sate for uploading loading screen image
    const [selectedBGImg, setSelectedBGImg] = useState(null);

    // state for updating pop up setting
    const [jsonEditorField, setJsonEditorField] = useState("");
    const [jsonEditorIndex, setJsonEditorIndex] = useState(0);

    // flag for differentiating metadata in setting and popup
    const [isMetaDataInSetting, setIsMetaDataInSetting] = useState(true);

    // banner
    const [bannerType, setBannerType] = useState("SUCCESS");
    const [successMessageBanner, setSuccessMessageBanner] = useState("");

    // error for file upload
    const [errorMessage, setErrorMessage] = useState(false);

    const { data: presetData } = useQuery(["roomPresets"], getAllRoomPreset, {
        ...CACHE_QUERY_OPTIONS(),
    });

    const [tagState, setTagState] = useState({
        clientTag: "",
        clientTagMeta: {},
        // achievementActive: false,
    });

    const [tagSettingState, setTagSettingState] = useState({
        clientTagSettingsId: "",
        presetId: "",
        settingPresetName: "",
        loadingScreenImageUrl: { default: "" },
        loadingMessage: { default: "" },
        loginText: "",
        loginUrl: "",
        metaData: {},
    });

    const [tagPopupState, setTagPopupState] = useState([
        {
            unityPopupId: "",
            isEnabled: false,
            triggerType: "",
            triggerValue: {},
            metadata: {},
        },
    ]);

    const [unityPopup, setUnityPopup] = useState([
        {
            popUpName: "",
            popUpType: "",
            popUpData: {},
            isActive: true,
        },
    ]);

    const {
        data: ClientTagData,
        isLoading,
        isError,
    } = useQuery(["clientTagByName", data.tag], () => getClientTagByName(data.tag), {
        ...CACHE_QUERY_OPTIONS(),
        enabled: !!data.tag,
    });
    const { data: popupData } = useQuery([""], getUnityPopups, {
        ...CACHE_QUERY_OPTIONS(),
    });

    useEffect(() => {
        if (!isLoading && !isError && ClientTagData && Object.keys(ClientTagData).length > 0) {
            setTagState({
                clientTag: ClientTagData.clientTag?.tag || "",
                clientTagMeta: ClientTagData.clientTag?.meta || {},
            });

            setTagSettingState({
                clientTagSettingsId: ClientTagData.clientTagSettings?.clientTagSettingsId || "",
                presetId: ClientTagData.clientTagSettings?.presetId || "",
                settingPresetName: ClientTagData.clientTagSettings?.settingPresetName || "",
                loadingScreenImageUrl: ClientTagData.clientTagSettings?.loadingScreenImageUrl || { default: "" },
                loadingMessage: ClientTagData.clientTagSettings?.loadingMessage || { default: "" },
                loginText: ClientTagData.clientTagSettings?.loginText || "",
                loginUrl: ClientTagData.clientTagSettings?.loginUrl || "",
                metaData: ClientTagData.clientTagSettings?.metaData || {},
            });

            setTagPopupState(
                Array.isArray(ClientTagData.clientTagPopups) && ClientTagData.clientTagPopups.length > 0
                    ? ClientTagData.clientTagPopups.map((popup) => ({
                          unityPopupId: popup.unityPopupId || "",
                          isEnabled: popup.isEnabled ?? false,
                          triggerType: popup.triggerType || "",
                          triggerValue: popup.triggerValue || {},
                          metadata: popup.metadata || {},
                      }))
                    : [
                          {
                              unityPopupId: "",
                              isEnabled: false,
                              triggerType: "",
                              triggerValue: {},
                              metadata: {},
                          },
                      ]
            );

            setUnityPopup(
                Array.isArray(ClientTagData.clientTagPopups) && ClientTagData.clientTagPopups.length > 0
                    ? ClientTagData.clientTagPopups.map((popup) => ({
                          popUpName: popup.popupName || "",
                          popUpType: popup.popupType || "",
                          popUpData: popup.popupData || {},
                          isActive: popup.isActive ?? true,
                      }))
                    : [
                          {
                              popUpName: "",
                              popUpType: "",
                              popUpData: {},
                              isActive: true,
                          },
                      ]
            );
        }
    }, [ClientTagData, isLoading, isError, data.tag]);

    // close modal and refetch data
    const resetStates = () => {
        setTimeout(() => {
            closeModal();
            window.location.reload();
        }, 1000);
    };

    // function to update tagState
    const updateTagState = (key, value) => {
        if (key === "name" && value.trim() === "") {
            setButtonDisabled(true);
        } else {
            setButtonDisabled(false);
        }

        setTagState((prevState) => ({
            ...prevState,
            [key]: value,
        }));
    };

    // function to update tagSettingState
    const updateTagSettingState = (key, value) => {
        if (key === "settingPresetName" && value.trim() === "") {
            setButtonDisabled(true);
        } else {
            setButtonDisabled(false);
        }

        if (key.includes(".")) {
            const [parent, child] = key.split(".");
            setTagSettingState((prevState) => ({
                ...prevState,
                [parent]: {
                    ...prevState[parent],
                    [child]: value,
                },
            }));
        } else {
            setTagSettingState((prevState) => ({
                ...prevState,
                [key]: value,
            }));
        }
    };

    // function to update popup setting
    const updateTagPopupState = (index, key, value) => {
        setTagPopupState((prevState) => {
            const newPopups = [...prevState];
            if (key === "triggerType") {
                newPopups[index] = {
                    ...newPopups[index],
                    triggerType: value,
                    triggerValue: {},
                };
            } else {
                newPopups[index] = {
                    ...newPopups[index],
                    [key]: value,
                };
            }
            setButtonDisabled(false);
            return newPopups;
        });
    };

    const addNewPopup = () => {
        setTagPopupState((prevState) => [
            ...prevState,
            {
                unityPopupId: "",
                isEnabled: false,
                triggerType: "",
                triggerValue: {},
                metaData: {},
            },
        ]);
    };

    const handleEditSubmit = async () => {
        if (activeTab === "CLIENT TAG") {
            try {
                let imageUrl = tagSettingState.loadingScreenImageUrl;

                if (selectedBGImg) {
                    const res = await uploadLoadingScreenImage(selectedBGImg);
                    if (res?.status === 200) {
                        console.log("Loading screen image uploaded successfully:", res);
                        imageUrl = res.data.presignedUrls[0].url;
                        console.log("Image URL:", imageUrl);
                        setTagSettingState((prevState) => ({
                            ...prevState,
                            loadingScreenImageUrl: { default: imageUrl },
                        }));
                    } else {
                        console.error("Error uploading loading screen image:", res);
                        setSuccessMessageBanner("Error uploading loading screen image");
                        setBannerType("ERROR");
                        return;
                    }
                }

                const BodyData = {
                    clientTag: tagState.clientTag,
                    clientTagMeta: tagState.clientTagMeta,
                    ClientTagSettings: {
                        presetId: tagSettingState.presetId,
                        settingPresetName: tagSettingState.settingPresetName,
                        loadingScreenImageUrl: { default: imageUrl },
                        loadingMessage: tagSettingState.loadingMessage,
                        loginText: tagSettingState.loginText,
                        loginUrl: tagSettingState.loginUrl,
                        metaData: tagSettingState.metaData,
                    },
                };

                // console.log("presetId for CLIENT TAG SETTING update:", tagSettingState.clientTagSettingsId);
                const response = await updateClientTag(BodyData, tagSettingState.clientTagSettingsId);
                if (response?.status === 200 || response?.status === 201) {
                    console.log("Client tag updated successfully:", response);
                    setSuccessMessageBanner("Client tag and setting updated successfully");
                    setBannerType("SUCCESS");
                    resetStates();
                } else {
                    console.error("Error updating client tag:", response);
                    setSuccessMessageBanner("Error updating client tag");
                    setBannerType("ERROR");
                }
            } catch (error) {
                console.error("Error updating client tag:", error);
                setSuccessMessageBanner("Error updating client tag");
                setBannerType("ERROR");
            }
        } else if (activeTab === "POP UP SETTING") {
            try {
                const BodyData = {
                    clientTag: tagState.clientTag,
                    clientTagPopups: tagPopupState.map((popup, index) => ({
                        unityPopupId: popup.unityPopupId,
                        isEnabled: popup.isEnabled,
                        triggerType: popup.triggerType,
                        triggerValue: popup.triggerValue,
                        metadata: popup.metadata,
                    })),
                };
                console.log("BodyData for POP UP SETTING update:", BodyData);
                const response = await updateClientTag(BodyData, tagSettingState.clientTagSettingsId);
                if (response?.status === 200 || response?.status === 201) {
                    console.log("Client tag pop up setting updated successfully:", response);
                    setSuccessMessageBanner("Client tag pop up setting updated successfully");
                    setBannerType("SUCCESS");
                    // close modal 1 second after success
                    resetStates();
                } else {
                    console.error("Error updating client tag pop up setting:", response);
                    setSuccessMessageBanner("Error updating client tag pop up setting");
                    setBannerType("ERROR");
                }
            } catch (error) {
                console.error("Error updating client tag pop up setting:", error);
                setSuccessMessageBanner("Error updating client tag pop up setting");
                setBannerType("ERROR");
            }
        }
    };

    const uploadLoadingScreenImage = (file) => {
        const data = {
            file,
            usageType: "roomDetailsImage",
        };
        return uploadFile(data, token)
            .then((response) => {
                if (response.status === 200) {
                    console.log("File uploaded successfully:", response);
                    const imageId = response.data.id;
                    return getPresignedUrl([imageId]);
                }
                console.error("File upload failed:", response);
                return response;
            })
            .catch((error) => {
                console.error("Error uploading file:", error);
                return { status: "ERROR", error };
            });
    };

    return (
        <>
            <Banner
                width={"100%"}
                zIndex="9999"
                message={`${successMessageBanner}`}
                type={bannerType}
                openBanner={!!successMessageBanner}
                setOpenBanner={setSuccessMessageBanner}
                topDisplay
                showCloseButton
                gameScreen={false}
                top={"0px"}
                left={"0"}
            />
            <ModalContent>
                <Typography variant="h2PurpleHeading" margin={`0 0 1rem 0`}>
                    Update Data
                </Typography>
                <NavContainer>
                    {tabs?.map((tab) => (
                        <Tab key={tab} active={activeTab === tab} onClick={() => setActiveTab(tab)}>
                            {tab}
                        </Tab>
                    ))}
                </NavContainer>

                {activeTab === "CLIENT TAG" && (
                    <Table>
                        <TableRow>
                            {/* <TableColumn>
                            <Label>Client Tag Name*</Label>
                            <InputBox
                                type="text"
                                id="clientTagName"
                                placeholder="Enter Client Tag Name"
                                value={tagState.clientTag}
                                onChange={(e) => updateTagState("clientTag", e.target.value)}
                            />
                        </TableColumn> */}
                        </TableRow>

                        <TableRow>
                            <TableColumn>
                                <Label>Client Tag Meta data</Label>
                                <InputBox
                                    type="text"
                                    name={"meta"}
                                    value={JSON.stringify(tagState.clientTagMeta || {})}
                                    onClick={() => {
                                        setIsJsonEditorOpen(true);
                                        setJsonEditorField("clientTagMeta");
                                        setJsonEditorValue(JSON.stringify(tagState.clientTagMeta || {}, null, 2));
                                    }}
                                />
                            </TableColumn>
                        </TableRow>


                        <UploadFile
                            label="loading screen Image"
                            accept={["image/png", "image/jpeg", "image/jpg"]}
                            selectedFile={selectedBGImg}
                            setSelectedFile={(file) => {
                                setSelectedBGImg(file);
                                if (file) {
                                    setButtonDisabled(false);
                                } else {
                                    setButtonDisabled(true);
                                }
                            }}
                            source="withoutCallingFileUploadAPI"
                        />

                        {errorMessage && (
                            <TextContent hasError fontsize={"14px"}>
                                {"File size should be less than 1MB"}
                            </TextContent>
                        )}

                        <TableRow>
                            <TableColumn>
                                <Label>Loading Message</Label>
                                <InputBox
                                    type="text"
                                    id="Client Tag Setting"
                                    placeholder="Loading Message"
                                    value={tagSettingState.loadingMessage.default}
                                    onChange={(e) => updateTagSettingState("loadingMessage.default", e.target.value)}
                                />
                            </TableColumn>
                        </TableRow>

                        <TableRow>
                            <TableColumn>
                                <Label>Login Text</Label>
                                <InputBox
                                    type="text"
                                    id="Client Tag Setting"
                                    placeholder="Login Text"
                                    value={tagSettingState.loginText}
                                    onChange={(e) => updateTagSettingState("loginText", e.target.value)}
                                />
                            </TableColumn>
                        </TableRow>

                        <TableRow>
                            <TableColumn>
                                <Label>Login Url</Label>
                                <InputBox
                                    type="text"
                                    id="Client Tag Setting"
                                    placeholder="Login Url"
                                    value={tagSettingState.loginUrl}
                                    onChange={(e) => updateTagSettingState("loginUrl", e.target.value)}
                                />
                            </TableColumn>
                        </TableRow>

                        <TableRow>
                            <TableColumn>
                                <Label>Client Tag Setting Meta data</Label>
                                <InputBox
                                    type="text"
                                    name={"metaData"}
                                    value={JSON.stringify(tagSettingState.metaData || {})}
                                    onClick={() => {
                                        setIsJsonEditorOpen(true);
                                        setJsonEditorField("metaData");
                                        setJsonEditorValue(JSON.stringify(tagSettingState.metaData || {}, null, 2));
                                    }}
                                />
                            </TableColumn>
                        </TableRow>

                        {/* <TableRow>
                            <Button
                                // type="submit"
                                variant={"PinkButton"}
                                buttonWidth={"100%"}
                                disabled={buttonDisabled}
                                label={`Update Client Tag Setting`}
                                onClick={() => {
                                    handleEditSubmit();
                                }}
                            />
                        </TableRow> */}

                        <TableRow>
                            <Button
                                // type="submit"
                                variant={"PinkButton"}
                                buttonWidth={"100%"}
                                disabled={buttonDisabled}
                                label={`Update Client Tag`}
                                onClick={() => {
                                    // console.log("update with new state:", tagState);
                                    handleEditSubmit();
                                }}
                            />
                        </TableRow>
                    </Table>
                )}

                {/* {activeTab === "CLIENT TAG SETTING" && (
                    
                        
                )} */}

                {activeTab === "POP UP SETTING" && (
                    <>
                        {isLoading && (
                            <LoadingIconContainer>
                                {" "}
                                <LoadingIcon />
                            </LoadingIconContainer>
                        )}
                        {!isLoading &&
                            tagPopupState.map((popup, index) => (
                                <div key={index} style={{ marginBottom: "2rem" }}>
                                    <Typography variant="h3" margin="1rem 0">
                                        Popup {index + 1}
                                    </Typography>
                                    <Table>
                                        <TableRow>
                                            <TableColumn>
                                                <Dropdown
                                                    key="popupDataInput"
                                                    label="Unity Pop up*"
                                                    items={
                                                        popupData?.unityPopups?.map((item) => ({
                                                            ...item,
                                                            value: item.popupName,
                                                        })) || []
                                                    }
                                                    selectedValue={popup.unityPopupId}
                                                    setSelectedValue={(value) => {
                                                        updateTagPopupState(
                                                            index,
                                                            "unityPopupId",
                                                            popupData?.unityPopups?.find(
                                                                (item) => item.popupName === value
                                                            )?.unityPopupId || ""
                                                        );
                                                    }}
                                                    placeholder="Select Unity Popup"
                                                />
                                            </TableColumn>
                                        </TableRow>

                                        <TableRow>
                                            <TableColumn>
                                                <Label>Is pop up enabled?</Label>
                                                <ToggleSwitch
                                                    value={popup.isEnabled}
                                                    onChange={(value) => updateTagPopupState(index, "isEnabled", value)}
                                                />
                                            </TableColumn>
                                        </TableRow>

                                        <TableRow>
                                            <TableColumn>
                                                <Dropdown
                                                    key={`triggerType-${index}`}
                                                    label="Popup trigger type"
                                                    items={["room_load", "achievement", "dynamic_linkout"].map(
                                                        (value) => ({
                                                            value,
                                                        })
                                                    )}
                                                    selectedValue={popup.triggerType}
                                                    setSelectedValue={(value) => {
                                                        updateTagPopupState(index, "triggerType", value);
                                                    }}
                                                    placeholder="Select Trigger Type"
                                                />
                                            </TableColumn>
                                        </TableRow>

                                        <TableRow>
                                            <TableColumn>
                                                {["dynamic_linkout", "room_load", "achievement"].includes(
                                                    popup.triggerType
                                                ) && (
                                                    <>
                                                        <Label>
                                                            {popup.triggerType === "dynamic_linkout" &&
                                                                "Popup trigger value"}
                                                            {popup.triggerType === "room_load" &&
                                                                "Popup trigger value for room load"}
                                                            {popup.triggerType === "achievement" &&
                                                                "Popup trigger value for achievement"}
                                                        </Label>
                                                        <InputBox
                                                            type="text"
                                                            id={`triggerValue-${index}`}
                                                            placeholder="Enter triggervalue"
                                                            value={JSON.stringify(popup.triggerValue || {})}
                                                            onClick={() => {
                                                                setJsonEditorField("triggerValue");
                                                                setJsonEditorValue(
                                                                    JSON.stringify(popup.triggerValue || {}, null, 2)
                                                                );
                                                                setJsonEditorIndex(index);
                                                                setIsJsonEditorOpen(true);
                                                            }}
                                                        />
                                                    </>
                                                )}
                                            </TableColumn>
                                        </TableRow>

                                        <TableRow>
                                            <TableColumn>
                                                <Label>Meta data</Label>
                                                <InputBox
                                                    type="text"
                                                    name={`metaData-${index}`}
                                                    value={JSON.stringify(popup.metaData || {})}
                                                    onClick={() => {
                                                        setIsMetaDataInSetting(false);
                                                        setJsonEditorField("metaData");
                                                        setJsonEditorValue(
                                                            JSON.stringify(popup.metaData || {}, null, 2)
                                                        );
                                                        setJsonEditorIndex(index);
                                                        setIsJsonEditorOpen(true);
                                                    }}
                                                />
                                            </TableColumn>
                                        </TableRow>
                                    </Table>
                                </div>
                            ))}
                        <TableRow>
                            <TableColumn>
                                <Button
                                    variant="SmallButton"
                                    buttonWidth="100%"
                                    label="Add Another Popup"
                                    onClick={addNewPopup}
                                />
                            </TableColumn>
                        </TableRow>

                        <Button
                            // type="submit"
                            variant={"PinkButton"}
                            buttonWidth={"100%"}
                            disabled={buttonDisabled}
                            label={`Update Pop Up Setting`}
                            onClick={() => {
                                console.log("update with new pop up state:", tagPopupState);
                                handleEditSubmit();
                            }}
                        />
                    </>
                )}
            </ModalContent>
            <>
                {isJsonEditorOpen && (
                    <Modal
                        isOpen={isJsonEditorOpen}
                        setIsOpen={setIsJsonEditorOpen}
                        children={
                            <JsonViewerModal
                                fieldName={jsonEditorField}
                                label="Edit JSON Data"
                                jsonEditorValue={jsonEditorValue}
                                closeModal={() => setIsJsonEditorOpen(false)}
                                handleJsonSave={(parsedData, fieldName) => {
                                    if (fieldName === "triggerValue") {
                                        updateTagPopupState(jsonEditorIndex, fieldName, parsedData);
                                    } else if (fieldName === "metaData") {
                                        if (isMetaDataInSetting) {
                                            updateTagSettingState("metaData", parsedData);
                                        } else {
                                            updateTagPopupState(jsonEditorIndex, fieldName, parsedData);
                                        }
                                    } else if (fieldName === "clientTagMeta") {
                                        updateTagState("clientTagMeta", parsedData);
                                    }
                                    setIsJsonEditorOpen(false);
                                }}
                            />
                        }
                    />
                )}
            </>
        </>
    );
};

export default EditTag;

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    color: ${setColor.inputText};
`;

const NavContainer = styled.div`
    display: flex;
    padding: 20px 40px;
    align-items: center;
    border-bottom: 1px solid #2a2a30;
`;

const Tab = styled.div`
    font-family: "Arial", sans-serif;
    font-size: 16px;
    letter-spacing: 2px;
    color: ${({ active }) => (active ? "#B9AFFF" : "#A0A0A0")};
    font-weight: 600;
    margin-right: 40px;
    cursor: pointer;
    position: relative;
    transition: color 0.3s ease;

    &:hover {
        color: #b9afff;
    }

    &::after {
        content: "";
        position: absolute;
        bottom: -25px;
        left: 0;
        width: ${({ active }) => (active ? "100%" : "0")};
        height: 4px;
        background-color: #b9afff;
        transition: width 0.3s ease;
    }
`;

const Table = styled.div`
    display: flex;
    flex-direction: column;
    padding: 1rem;
    border-radius: 5px;
    color: #fbfcff;
`;

const TableRow = styled.div`
    display: flex;
    flex-direction: row;
    margin-bottom: 10px;
`;

const TableColumn = styled.div`
    flex: 1;
    padding: 5px;
`;

const Label = styled.div`
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 10px;
`;

const InputBox = styled.input`
    padding: 10px;
    border: ${(props) => (props.errorMessage ? `1px solid ${setColor.errorColor}` : `1px solid ${setColor.grey1}`)};
    border-radius: 5px;
    color: ${setColor.inputText};
    margin: 0rem 0rem 0rem 0rem;
    width: ${(props) => (props.width ? props.width : "100%")};
    background-color: transparent;
    margin-bottom: ${(props) => (props.errorMessage ? "0.5rem" : "0rem")};
    &:disabled {
        background-color: ${setColor.grey1};
    }
`;

const TextContent = styled.p`
    color: ${(props) => (props.hasError ? setColor.errorColor : setColor.circularProgress)};
    text-align: left;
    font-family: ${setFont.barlow}, sans-serif;
    font-size: ${(props) => (props.fontsize ? props.fontsize : "13px")};
    font-style: normal;
    line-height: normal;
    margin: 5px;
    letter-spacing: 0.5px;
    font-weight: 400;

    @media (max-width: 768px) {
        margin-bottom: 20px;
    }
`;

const TagsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
`;

const Tag = styled.div`
    display: flex;
    align-items: center;
    background: ${setColor.grey1};
    border-radius: 16px;
    padding: 4px 12px;
    margin-right: 8px;
    margin-bottom: 8px;
`;

const RemoveButton = styled.button`
    margin-left: 8px;
    background: transparent;
    border: none;
    cursor: pointer;
    color: #d00;
    font-weight: bold;
`;

const LoadingIconContainer = styled.div`
    margin-top: 100px;
    display: flex;
    justify-content: center;
    width: 100%;
`;
